/**
 * SpendingByCategoryChart - Donut chart showing expense breakdown by category
 *
 * Features:
 * - Donut chart with percentage labels
 * - Legend showing top 5 categories with amounts
 * - Total spending summary
 * - Loading skeleton state
 * - Empty state for no data
 */

import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Loader2, PieChart as PieChartIcon } from 'lucide-react';
import type { SpendingReportResponse, SpendingCategoryData } from '@/types/accounting';

interface SpendingByCategoryChartProps {
  /** Spending report data from API */
  data: SpendingReportResponse | null;
  /** Whether data is currently loading */
  loading: boolean;
}

/**
 * Format cents to currency display using EUR
 */
function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

/**
 * Loading skeleton for the chart
 */
function ChartSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse" data-testid="spending-chart-skeleton">
      <div className="h-5 bg-gray-200 rounded w-48 mb-6" />
      <div className="flex items-center gap-8">
        <div className="w-80 h-80 flex items-center justify-center">
          <div className="w-64 h-64 rounded-full border-[24px] border-gray-200" />
        </div>
        <div className="flex-1 space-y-4">
          <div className="text-center">
            <div className="h-8 bg-gray-200 rounded w-32 mx-auto mb-2" />
            <div className="h-4 bg-gray-200 rounded w-24 mx-auto" />
          </div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gray-200" />
                <div className="h-4 bg-gray-200 rounded w-24" />
              </div>
              <div className="text-right">
                <div className="h-4 bg-gray-200 rounded w-16 mb-1" />
                <div className="h-3 bg-gray-200 rounded w-10" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Empty state when no spending data is available
 */
function EmptyState() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="spending-chart-empty">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Spending Distribution
      </h3>
      <div className="py-12 text-center">
        <PieChartIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h4 className="text-lg font-semibold text-gray-700 mb-2">
          No Spending Data
        </h4>
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          No classified expenses found for the selected date range.
          Try adjusting your filters or classifying some transactions.
        </p>
      </div>
    </div>
  );
}

export function SpendingByCategoryChart({ data, loading }: SpendingByCategoryChartProps) {
  // Show skeleton while loading
  if (loading && !data) {
    return <ChartSkeleton />;
  }

  // Show empty state if no data or no categories
  if (!data || data.categories.length === 0) {
    return <EmptyState />;
  }

  const chartData = data.categories.map((category: SpendingCategoryData) => ({
    name: category.accountName,
    value: category.amountCents / 100,
    percentage: category.percentage,
    color: category.color,
  }));

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="spending-chart">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Spending Distribution
        {loading && (
          <Loader2 className="w-4 h-4 ml-2 inline-block animate-spin text-gray-400" />
        )}
      </h3>
      <div className="flex items-center gap-8">
        <div className="w-80 h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={70}
                outerRadius={120}
                paddingAngle={2}
                label={({ name, percent }) =>
                  `${name}: ${((percent ?? 0) * 100).toFixed(1)}%`
                }
                labelLine={{ stroke: '#9CA3AF', strokeWidth: 1 }}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => formatCurrency((value as number) * 100)}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex-1">
          <div className="text-center mb-6">
            <div className="text-3xl font-bold text-gray-900" data-testid="total-spending">
              {formatCurrency(data.totalCents)}
            </div>
            <div className="text-sm text-gray-500">Total Spending</div>
          </div>
          <div className="space-y-2" data-testid="category-list">
            {data.categories.slice(0, 5).map((category: SpendingCategoryData) => (
              <div
                key={category.accountId}
                className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                data-testid={`category-item-${category.accountId}`}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: category.color }}
                  />
                  <span className="text-sm text-gray-700">{category.accountName}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {formatCurrency(category.amountCents)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {category.percentage.toFixed(1)}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
